import { createContext, useContext } from 'react';

import type { PatientSession } from '../../api/patientPortal/auth.ts';

/**
 * The patient portal's own session context — deliberately a separate `createContext` from
 * `web/src/auth/sessionContext.ts`'s staff `SessionContext`, matching every other "own module, own
 * cookie" decision in this directory (`docs/decisions/patient-portal-frontend-scope.md`). A patient
 * page has no reason to ever read a staff session, and mixing the two into one context would make
 * that mistake possible instead of a type error.
 */

export const PATIENT_SESSION_QUERY_KEY = ['patientPortalSession'] as const;

export const PatientSessionContext = createContext<PatientSession | null>(null);

/**
 * The signed-in patient session. Throws outside `<RequirePatientSession>`, matching `useSession`'s
 * own reasoning: a component only reachable behind the guard should not have to carry a branch for
 * "there is no session," since that branch can never actually run.
 */
export function usePatientSession(): PatientSession {
  const session = useContext(PatientSessionContext);
  if (session === null) {
    throw new Error('usePatientSession must be used inside <RequirePatientSession>');
  }
  return session;
}
